import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FulfilmentService } from '@woolworthsnz/fulfilment';
import {
	DatalayerService,
	FlexDirection,
	HistoryService,
	NotificationType,
	RoutedModalBaseComponent,
	TrackingEvent,
	ButtonComponent,
	SlottedModalComponent,
	SvgIconComponent,
} from '@woolworthsnz/styleguide';
import { first, map, tap } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import { MODAL_ROUTES } from '@woolworthsnz/shop';
import { AsyncPipe } from '@angular/common';

@Component({
	selector: 'wnz-change-pickup-store-modal',
	templateUrl: './change-pickup-store-modal.component.html',
	styleUrls: ['./change-pickup-store-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [SlottedModalComponent, SvgIconComponent, ButtonComponent, AsyncPipe],
})
export class ChangePickupStoreModalComponent extends RoutedModalBaseComponent implements OnDestroy {
	mhlsPickupAddressId: number;
	mhlsPickupStoreName: string;
	selectedStoreName$: Observable<string>;
	modalFooterButtonDirection = FlexDirection.COLUMN;

	private changePickupStoreSub?: Subscription;
	private selectedStoreName: string;

	constructor(
		router: Router,
		historyService: HistoryService,
		private fulfilmentService: FulfilmentService,
		private dataLayerService: DatalayerService,
		protected activatedRoute: ActivatedRoute
	) {
		super(router, activatedRoute, historyService);
		const state = this.router.getCurrentNavigation()?.extras.state;

		if (state) {
			this.mhlsPickupAddressId = state.mhlsPickupAddressId;
			this.mhlsPickupStoreName = state.mhlsPickupStoreName;
		}

		if (this.fulfilmentService.method === 'Pickup') {
			this.selectedStoreName$ = of(this.fulfilmentService.pickupAddress || '');
		} else {
			this.selectedStoreName$ = this.fulfilmentService
				.fetchFulfilmentStore(this.fulfilmentService.fulfilmentStoreId || 0)
				.pipe(map((store) => (store?.name ? store.name.replace('CD', 'Countdown') : '')));
		}

		this.selectedStoreName$.pipe(tap((name) => (this.selectedStoreName = name)));
	}

	ngOnDestroy(): void {
		if (this.changePickupStoreSub && !this.changePickupStoreSub.closed) {
			this.changePickupStoreSub.unsubscribe();
		}
	}

	onClose(): void {
		this.dataLayerService.trackEvent(
			TrackingEvent.NotificationEvent,
			NotificationType.Modal,
			'stock availability close',
			'store change'
		);

		this.closeModal();
	}

	onChangePickupStore(): void {
		this.dataLayerService.trackEvent(
			TrackingEvent.NotificationEvent,
			NotificationType.Modal,
			'stock availability change stores',
			this.mhlsPickupStoreName
		);

		this.changePickupStoreSub = this.fulfilmentService
			.changeSelectedPickupStore(this.mhlsPickupAddressId)
			.pipe(first())
			.subscribe(
				() => {
					this.close();
				},
				() => {
					this.close();
				}
			);
	}

	onKeepStore(): void {
		this.dataLayerService.trackEvent(
			TrackingEvent.NotificationEvent,
			NotificationType.Modal,
			'stock availability keep store',
			this.selectedStoreName
		);

		this.closeModal();
	}

	private closeModal(): void {
		const prevUrl = this.historyService.getPreviousNonModalUrlForOutlet(MODAL_ROUTES.PRODUCT_DETAILS_MODAL);
		const parsedUrl = this.router.parseUrl(prevUrl);

		parsedUrl.queryParams = {
			stockcode: parsedUrl.queryParams.stockcode,
			name: parsedUrl.queryParams.name,
			ostore: this.fulfilmentService.fulfilmentStoreId,
		};

		const url = this.router.serializeUrl(parsedUrl);
		this.close({ url });
	}
}
